import type {
  CargoOnboardEntry,
  CargoTypeReference,
  UnitOfMeasurementReference,
} from "@services/vesselCall/types";

export type CargoOnboardFormItem = {
  id: string;
  cargoType: string;
  cargoTypeDescription?: string;
  quantity?: string;
  unitOfMeasurement: string;
  unitOfMeasurementDescription?: string;
  hazardousAndDangerousCargo: boolean;
  imoHazardClasses?: string;
};

export type CargoOnboardGridItem = {
  id: string;
  cargoType: string;
  quantity: number | null;
  unitOfMeasurement: string;
  hazardousAndDangerousCargo: boolean;
  imoHazardClasses: string;
};

export const CARGO_ONBOARD_QUANTITY_MAX_DIGITS = 10;
export const CARGO_ONBOARD_QUANTITY_MAX_DECIMALS = 3;
export const CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH = 200;

type NormalizableText = string | number | boolean | null | undefined;

type VesselConditionLike =
  | string
  | {
      value?: string | null;
      label?: string | null;
      conditionCode?: string | null;
      conditionDesc?: string | null;
    }
  | null
  | undefined;

type CargoOnboardApiEntry = CargoOnboardEntry & {
  cargoTypeName?: string | null;
};

const normalizeText = (value: NormalizableText): string =>
  typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();

const normalizeVesselCondition = (value: VesselConditionLike): string => {
  const condition = value && typeof value === "object" ? value : undefined;
  const rawValue =
    condition?.value ??
    condition?.conditionCode ??
    condition?.label ??
    condition?.conditionDesc ??
    (typeof value === "string" ? value : undefined);

  return normalizeText(rawValue)
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
};

const BALLAST_CONDITION_ALIASES = new Set(["BALLAST", "IN_BALLAST", "B"]);
const CARGO_VISIBLE_CONDITION_ALIASES = new Set([
  "LOADED",
  "L",
  "LD",
  "LADEN",
  "PARTIALLY_LOADED",
  "PARTIALLY_LADEN",
  "PL",
]);

const normalizeQuantity = (value: string | number | null | undefined): number | null => {
  if (value === "" || value == null) return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getCargoTypeReference = (
  value: CargoOnboardApiEntry["cargoType"],
): CargoTypeReference | undefined => (value && typeof value === "object" ? value : undefined);

const getUnitOfMeasurementReference = (
  value: CargoOnboardApiEntry["unitOfMeasurement"],
): UnitOfMeasurementReference | undefined =>
  value && typeof value === "object" ? value : undefined;

const toFormQuantity = (value: string | number | null | undefined): string | undefined => {
  const normalized = normalizeQuantity(value);
  return normalized === null ? undefined : String(normalized);
};

const createFallbackId = (index: number) => `cargo-onboard-${index}`;

export const isBallastVesselCondition = (value?: VesselConditionLike): boolean => {
  const normalized = normalizeVesselCondition(value);
  return BALLAST_CONDITION_ALIASES.has(normalized);
};

export const shouldShowCargoOnboardSection = (value?: VesselConditionLike): boolean => {
  const normalized = normalizeVesselCondition(value);
  return CARGO_VISIBLE_CONDITION_ALIASES.has(normalized);
};

export const hasCargoOnboardValues = (item?: Partial<CargoOnboardFormItem> | null): boolean => {
  if (!item) return false;

  return Boolean(
    normalizeText(item.cargoType) ||
      normalizeText(item.cargoTypeDescription) ||
      normalizeText(item.unitOfMeasurement) ||
      normalizeText(item.unitOfMeasurementDescription) ||
      normalizeText(item.imoHazardClasses) ||
      normalizeQuantity(item.quantity) !== null ||
      item.hazardousAndDangerousCargo,
  );
};

export const sanitizeCargoOnboardItems = (
  items?: Array<Partial<CargoOnboardFormItem> | null> | null,
): CargoOnboardFormItem[] => {
  if (!Array.isArray(items)) return [];

  return items.filter(hasCargoOnboardValues).map((item, index) => ({
    id: normalizeText(item?.id) || createFallbackId(index),
    cargoType: normalizeText(item?.cargoType),
    cargoTypeDescription: normalizeText(item?.cargoTypeDescription) || undefined,
    quantity: toFormQuantity(item?.quantity),
    unitOfMeasurement: normalizeText(item?.unitOfMeasurement),
    unitOfMeasurementDescription: normalizeText(item?.unitOfMeasurementDescription) || undefined,
    hazardousAndDangerousCargo: Boolean(item?.hazardousAndDangerousCargo),
    imoHazardClasses: normalizeText(item?.imoHazardClasses) || undefined,
  }));
};

export const mapApiCargoOnboardToForm = (
  cargoOnboard?: CargoOnboardApiEntry[] | null,
): CargoOnboardFormItem[] => {
  if (Array.isArray(cargoOnboard)) {
    return sanitizeCargoOnboardItems(
      cargoOnboard.map((item, index) => {
        const raw = item ?? {};
        const cargoType = getCargoTypeReference(raw.cargoType);
        const unitOfMeasurement = getUnitOfMeasurementReference(raw.unitOfMeasurement);

        return {
          id: normalizeText(raw.id) || createFallbackId(index),
          cargoType:
            normalizeText(raw.cargoTypeReferenceCode) ||
            normalizeText(cargoType?.cargoTypeCode) ||
            normalizeText(raw.cargoTypeCode),
          cargoTypeDescription:
            (typeof raw.cargoType === "string" ? normalizeText(raw.cargoType) : "") ||
            normalizeText(cargoType?.cargoTypeDescription) ||
            normalizeText(cargoType?.cargoTypeDesc) ||
            normalizeText(raw.cargoTypeDescription) ||
            normalizeText(raw.cargoTypeDesc) ||
            normalizeText(raw.cargoTypeName),
          quantity: toFormQuantity(raw.cargoQuantity ?? raw.quantity),
          unitOfMeasurement:
            normalizeText(unitOfMeasurement?.unitCode) ||
            normalizeText(raw.cargoMeasurementUnit) ||
            (typeof raw.unitOfMeasurement === "string" ? normalizeText(raw.unitOfMeasurement) : ""),
          unitOfMeasurementDescription:
            normalizeText(unitOfMeasurement?.unitDescription) ||
            normalizeText(raw.unitOfMeasurementDescription) ||
            normalizeText(raw.unitDescription) ||
            normalizeText(raw.cargoMeasurementUnit) ||
            (typeof raw.unitOfMeasurement === "string" ? normalizeText(raw.unitOfMeasurement) : ""),
          hazardousAndDangerousCargo:
            typeof raw.cargoDangerous === "boolean"
              ? raw.cargoDangerous
              : typeof raw.dangerous === "boolean"
                ? raw.dangerous
                : Boolean(raw.hazardousAndDangerousCargo),
          imoHazardClasses:
            normalizeText(raw.cargoImoHazardClasses) || normalizeText(raw.imoHazardClasses),
        };
      }),
    );
  }

  return [];
};

export const mapCargoOnboardFormToApi = (
  items?: Array<Partial<CargoOnboardFormItem> | null> | null,
): CargoOnboardEntry[] =>
  sanitizeCargoOnboardItems(items).map((item) => ({
    cargoType: item.cargoTypeDescription || item.cargoType || undefined,
    cargoTypeReferenceCode: item.cargoType || undefined,
    cargoQuantity: normalizeQuantity(item.quantity),
    cargoMeasurementUnit: item.unitOfMeasurementDescription || item.unitOfMeasurement || undefined,
    cargoDangerous: Boolean(item.hazardousAndDangerousCargo),
    cargoImoHazardClasses: item.imoHazardClasses || null,
  }));

export const mapCargoOnboardItemsToGrid = (
  items?: Array<Partial<CargoOnboardFormItem> | null> | null,
): CargoOnboardGridItem[] =>
  sanitizeCargoOnboardItems(items).map((item) => ({
    id: item.id,
    cargoType: item.cargoTypeDescription || item.cargoType,
    quantity: normalizeQuantity(item.quantity),
    unitOfMeasurement: item.unitOfMeasurementDescription || item.unitOfMeasurement,
    hazardousAndDangerousCargo: Boolean(item.hazardousAndDangerousCargo),
    imoHazardClasses: item.imoHazardClasses || "",
  }));
